import { useEffect } from "react";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";

const faqItems = [
  {
    question: "What types of health insurance plans does HealthMarkets offer?",
    answer:
      "We offer a comprehensive range of private health insurance plans including Individual & Family Plans (ACA/Marketplace), Medicare Advantage, Medicare Supplement (Medigap), Part D Prescription Drug Plans, Dental & Vision Insurance, Short-Term Health Plans, Critical Illness coverage, and Group Health Insurance for small businesses. Our licensed agents in Irving, TX help you compare plans from 50+ top carriers.",
  },
  {
    question: "Is HealthMarkets Insurance Agency open 24 hours a day?",
    answer:
      "Yes! HealthMarkets Insurance Agency is open 24 hours a day, 7 days a week — including weekends and holidays. You can call us anytime at 972-217-4662 or visit our office at 1800 Market Pl Blvd #140, Irving, TX 75063. We understand health insurance questions don't follow a 9-to-5 schedule.",
  },
  {
    question: "Does it cost anything to use HealthMarkets Insurance services?",
    answer:
      "No! Our services are completely free to you. As independent insurance agents, we are compensated by the insurance carriers, not by our clients. You get the same price (or better) than going directly to an insurer, plus you get our expert guidance at zero cost to you.",
  },
  {
    question: "What is the difference between Medicare Advantage and Medicare Supplement?",
    answer:
      "Medicare Advantage (Part C) replaces Original Medicare and is offered by private insurance companies. It often includes extra benefits like dental, vision, and hearing. Medicare Supplement (Medigap) works alongside Original Medicare to help cover out-of-pocket costs like copays and deductibles. Our agents can walk you through both options to find the right fit for your healthcare needs and budget.",
  },
  {
    question: "Can HealthMarkets help me if I missed Open Enrollment?",
    answer:
      "Possibly yes! If you experience a qualifying life event (job loss, marriage, divorce, birth of a child, relocation, etc.) you may qualify for a Special Enrollment Period. Additionally, short-term health plans may be available year-round. Call us at 972-217-4662 and we'll evaluate your eligibility options right away.",
  },
  {
    question: "How do I know which health insurance plan is right for my family?",
    answer:
      "Our licensed agents perform a thorough needs assessment based on your family size, current health conditions, preferred doctors/hospitals, prescription medications, and budget. We then compare plans across 50+ carriers to find the best fit. There's no pressure — we educate and guide, and you decide. Schedule a free consultation at our Irving, TX office or by phone anytime.",
  },
  {
    question: "Does HealthMarkets offer health insurance for small businesses?",
    answer:
      "Yes! We specialize in group health insurance for small businesses of all sizes in the Irving, TX area. We help employers set up group health, dental, vision, life, and disability plans that attract and retain employees. We'll also explain employer contribution strategies and compliance requirements.",
  },
  {
    question: "What documents do I need to enroll in a health insurance plan?",
    answer:
      "Typically you'll need a government-issued ID, Social Security numbers for each enrollee, income information (for ACA plans), current insurance information (if applicable), and a list of current medications and preferred doctors. Our agents will guide you through exactly what's needed for your specific plan type.",
  },
];

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "InsuranceAgency",
  name: "HealthMarkets Insurance Agency",
  image: `${siteUrl}/og-image.jpg`,
  url: siteUrl,
  telephone: "+19722174662",
  priceRange: "Free Consultation",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1800 Market Pl Blvd #140",
    addressLocality: "Irving",
    addressRegion: "TX",
    postalCode: "75063",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 32.8784,
    longitude: -96.9464,
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    opens: "00:00",
    closes: "23:59",
  },
  hasMap: "https://maps.app.goo.gl/grzX3dievhN3giBv8",
  sameAs: ["https://maps.app.goo.gl/grzX3dievhN3giBv8"],
  description:
    "HealthMarkets Insurance Agency in Irving, TX offers personalized private health insurance plans including Individual, Family, Medicare & supplemental coverage. Licensed agents available 24/7. Call 972-217-4662.",
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "HealthMarkets Insurance Agency",
  url: siteUrl,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${siteUrl}/?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "HealthMarkets Insurance Agency Irving TX | Private Health Insurance 24/7",
  description:
    "HealthMarkets Insurance Agency — Irving, TX. Call 972-217-4662 anytime, open 24/7. Personalized private health insurance plans including Individual, Family, Medicare & supplemental coverage.",
  url: siteUrl,
  inLanguage: "en-US",
  isPartOf: {
    "@type": "WebSite",
    name: "HealthMarkets Insurance Agency",
    url: siteUrl,
  },
  about: {
    "@type": "InsuranceAgency",
    name: "HealthMarkets Insurance Agency",
  },
  dateModified: "2026-03-23",
};

function injectScript(id: string, data: object): HTMLScriptElement {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.textContent = JSON.stringify(data, null, 2);
  document.head.appendChild(script);
  return script;
}

export default function SEOJsonLD() {
  useEffect(() => {
    const s1 = injectScript("schema-local-business", localBusinessSchema);
    const s2 = injectScript("schema-website", websiteSchema);
    const s3 = injectScript("schema-faq", faqSchema);
    const s4 = injectScript("schema-webpage", webPageSchema);

    return () => {
      s1.remove();
      s2.remove();
      s3.remove();
      s4.remove();
    };
  }, []);

  return null;
}
